<template>
  <div
    class="brand-switch-animator"
    :style="{
      '--brand-switch-duration': `${animationDuration}ms`,
      '--brand-switch-loading-pulse-delay': `${animationDuration + 200}ms`,
      '--brand-switch-easing': `${animationEasing}`,
    }"
    :class="{
      'brand-switch-animator--active': isActive,
      'brand-switch-animator--started': hasStarted,
      'brand-switch-animator--finishing': isFinishing,
    }"
  >
    <div
      class="brand-switch-animator__content"
      :inert="isInert"
      @transitionend="onContentTransitionEnd"
    >
      <slot />
    </div>

    <div class="brand-switch-animator__darken"></div>

    <div
      class="brand-switch-animator__overlay"
      :style="{
        'background-color': brandColor,
      }"
    >
      <img
        class="brand-logo"
        :src="brandLogo || shopConfigStore.logo?.path"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
const { animationDuration, animationEasing, animationStage, brandColor, brandLogo } =
  storeToRefs(useBrandSwitchAnimationStore())
const shopConfigStore = useShopConfigStore()

const isActive = computed(() => animationStage.value !== 'idle')
const hasStarted = computed(() => animationStage.value === 'started')
const isFinishing = computed(() => animationStage.value === 'finishing')

const isInert = computed(() => isActive.value)

// After the brand switch "finishing" transition completes (scale back to 1),
// dispatch a scroll event on `document` so VParallax recalculates its position.
// VParallax listens on `document` (not `window`) and uses getBoundingClientRect()
// which returns wrong values while the CSS scale transform is active.
function onContentTransitionEnd(event: TransitionEvent) {
  if (event.propertyName === 'transform' && isFinishing.value) {
    document.dispatchEvent(new Event('scroll'))
  }
}
</script>

<style lang="scss" scoped>
@use '@fire-framework/styles/responsive';

.brand-switch-animator {
  display: contents;
}

.brand-switch-animator__content {
  transform-origin: 50vw 50vh; // Center of the viewport
  transition: transform var(--brand-switch-duration) var(--brand-switch-easing);

  .brand-switch-animator--active & {
    position: relative;
  }

  .brand-switch-animator--started & {
    transform: scale(0.8);
  }

  .brand-switch-animator--finishing & {
    transform: scale(1);
  }
}

.brand-switch-animator__darken {
  position: fixed;
  inset: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  z-index: 9998;
  pointer-events: none;
  background-color: rgba(0, 0, 0, 0.2);
  transition: opacity var(--brand-switch-duration) var(--brand-switch-easing);

  .brand-switch-animator--active & {
    opacity: 1;
  }

  .brand-switch-animator--finishing & {
    transition-delay: 0.2s;
    opacity: 0;
  }
}

.brand-switch-animator__overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: #000000;
  z-index: 9999;
  transform: translateY(-100%);
  visibility: hidden;

  .brand-switch-animator--active & {
    visibility: visible;
  }

  .brand-switch-animator--started & {
    transition: transform var(--brand-switch-duration) var(--brand-switch-easing);
    transform: translateY(0);
  }

  .brand-switch-animator--finishing & {
    transform: translateY(100%);
    transition: transform var(--brand-switch-duration) var(--brand-switch-easing);
  }
}

.brand-logo {
  max-width: 160px;
  max-height: 20px;
  transform: translateY(-50vh);

  .brand-switch-animator--started & {
    transition: transform var(--brand-switch-duration) 0.1s var(--brand-switch-easing);
  }

  .brand-switch-animator--active & {
    transform: translateY(0);
    animation: fadeInOut 3s var(--brand-switch-loading-pulse-delay) ease infinite;
  }

  @include responsive.tablet {
    max-height: 24px;
  }

  @include responsive.desktop {
    max-height: 32px;
  }
}

@keyframes fadeInOut {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.15);
  }
  100% {
    transform: scale(1);
  }
}
</style>
